import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  ChevronDown,
  LogOut,
  Menu as MenuIcon,
  Monitor,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Sun,
  UserRound,
  X,
} from 'lucide-react';
import {
  listarNotificacoes,
  marcarLida,
  marcarTodasLidas,
  type UserNotification,
} from '@/features/notifications/notifications.api';
import { Logo } from '@/components/Logo';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '@/components/Menu';
import { Skeleton } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { NAV_GROUPS, itemAtivo, type NavItem } from '@/app/nav';
import { ROTULO_DE_TEMA, useThemeStore } from '@/app/theme';
import { useAuthStore } from '@/features/auth/auth.store';

/**
 * Casca da aplicação: lateral, cabeçalho e área de conteúdo.
 *
 * A lateral recolhe para dar espaço às telas densas — lista de chamados com
 * muitas colunas — e o estado é lembrado, porque quem recolhe uma vez recolhe
 * sempre. No celular ela vira gaveta: não há largura para as duas coisas.
 */

const CHAVE_LATERAL = 'central.sidebar';

export function AppShell({ children }: { children: ReactNode }) {
  const [recolhida, setRecolhida] = useState(() => localStorage.getItem(CHAVE_LATERAL) === '1');
  const [gavetaAberta, setGavetaAberta] = useState(false);
  const pathname = useRouterState({ select: (estado) => estado.location.pathname });

  useEffect(() => {
    localStorage.setItem(CHAVE_LATERAL, recolhida ? '1' : '0');
  }, [recolhida]);

  // Trocar de rota fecha a gaveta: no celular ela cobre o conteúdo, e deixá-la
  // aberta esconderia justamente a tela que a pessoa acabou de pedir.
  useEffect(() => {
    setGavetaAberta(false);
  }, [pathname]);

  useEffect(() => {
    if (!gavetaAberta) return;
    const aoTeclar = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setGavetaAberta(false);
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [gavetaAberta]);

  return (
    <div className="relative flex h-full bg-background">
      <a
        href="#conteudo"
        className={cn(
          'sr-only z-50 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground',
          'focus:not-sr-only focus:absolute focus:left-4 focus:top-4',
        )}
      >
        Pular para o conteúdo
      </a>

      {/* Lateral fixa a partir de md; no celular quem manda é a gaveta. */}
      <Sidebar
        className={cn('hidden md:flex', recolhida ? 'w-16' : 'w-60')}
        recolhida={recolhida}
        pathname={pathname}
        onAlternarRecolhida={() => setRecolhida((valor) => !valor)}
      />

      {gavetaAberta ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            aria-label="Fechar navegação"
            onClick={() => setGavetaAberta(false)}
            className="absolute inset-0 animate-fade-in bg-foreground/40"
          />
          <Sidebar
            className="absolute inset-y-0 left-0 w-64 animate-slide-up shadow-elevation"
            recolhida={false}
            pathname={pathname}
            autoFocar
            onFechar={() => setGavetaAberta(false)}
          />
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header onAbrirGaveta={() => setGavetaAberta(true)} />

        <main id="conteudo" tabIndex={-1} className="flex-1 overflow-y-auto focus:outline-none">
          <div className="w-full px-3 py-3 sm:px-4">{children}</div>
        </main>

        <StatusBar />
      </div>
    </div>
  );
}

// ── lateral ────────────────────────────────────────────────────────────────

function Sidebar({
  className,
  recolhida,
  pathname,
  autoFocar = false,
  onAlternarRecolhida,
  onFechar,
}: {
  className?: string;
  recolhida: boolean;
  pathname: string;
  /** Leva o foco para o primeiro item ao montar — usado pela gaveta. */
  autoFocar?: boolean;
  onAlternarRecolhida?: () => void;
  onFechar?: () => void;
}) {
  const ativo = itemAtivo(pathname);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // A gaveta cobre a tela, e o botão que a abriu fica atrás da cortina:
    // deixar o foco lá faria o Tab caminhar por controles invisíveis.
    if (autoFocar) navRef.current?.querySelector<HTMLElement>('a[data-nav]')?.focus();
  }, [autoFocar]);

  /**
   * Setas percorrem a navegação em laço.
   *
   * O Tab já alcança cada link, mas numa lista vertical a expectativa é a
   * seta — e sem ela quem navega por teclado atravessa a lateral inteira para
   * chegar ao conteúdo.
   */
  function aoTeclar(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const links = [...event.currentTarget.querySelectorAll<HTMLElement>('a[data-nav]')];
    const atual = links.indexOf(document.activeElement as HTMLElement);
    if (atual === -1) return;
    event.preventDefault();
    const passo = event.key === 'ArrowDown' ? 1 : links.length - 1;
    links[(atual + passo) % links.length]?.focus();
  }

  return (
    <aside
      className={cn(
        'flex flex-none flex-col border-r border-white/[0.07] bg-sidebar text-sidebar-foreground',
        'transition-[width] duration-200 motion-reduce:transition-none',
        className,
      )}
    >
      <div
        className={cn(
          'flex h-11 flex-none items-center border-b border-white/[0.07]',
          recolhida ? 'justify-center px-2' : 'justify-between px-3',
        )}
      >
        <Logo compact={recolhida} />
        {onFechar ? (
          <button
            type="button"
            onClick={onFechar}
            aria-label="Fechar navegação"
            className="grid h-8 w-8 place-items-center rounded-md text-sidebar-foreground/60 transition-colors hover:bg-white/[0.08] hover:text-sidebar-foreground focus-visible:shadow-glow focus-visible:outline-none"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
      </div>

      <nav
        ref={navRef}
        aria-label="Navegação principal"
        onKeyDown={aoTeclar}
        className="flex-1 overflow-y-auto p-2"
      >
        {NAV_GROUPS.map((grupo, idx) => (
          <div key={grupo.id} className={idx > 0 ? 'mt-2' : undefined}>
            {!recolhida ? (
              <p
                className="mb-px select-none px-2 pt-3 text-[9px] font-semibold uppercase tracking-widest text-sidebar-foreground/30"
                aria-hidden
              >
                {grupo.label}
              </p>
            ) : idx > 0 ? (
              <div className="mb-1 mt-1 h-px bg-white/[0.08]" aria-hidden />
            ) : null}
            <div className="space-y-px">
              {grupo.items.map((item) => (
                <NavLink key={item.to} item={item} ativo={ativo === item.to} recolhida={recolhida} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {onAlternarRecolhida ? (
        <div className="flex-none border-t border-white/[0.07] p-1.5">
          <button
            type="button"
            onClick={onAlternarRecolhida}
            aria-label={recolhida ? 'Expandir navegação' : 'Recolher navegação'}
            className={cn(
              'flex w-full items-center gap-2 rounded px-2 py-[5px] text-[12px] text-sidebar-foreground/45',
              'transition-colors hover:bg-white/[0.08] hover:text-sidebar-foreground',
              'focus-visible:shadow-glow focus-visible:outline-none',
              recolhida && 'justify-center',
            )}
          >
            {recolhida ? (
              <PanelLeftOpen className="h-3.5 w-3.5 flex-none" aria-hidden />
            ) : (
              <>
                <PanelLeftClose className="h-3.5 w-3.5 flex-none" aria-hidden />
                Recolher
              </>
            )}
          </button>
        </div>
      ) : null}
    </aside>
  );
}

function NavLink({ item, ativo, recolhida }: { item: NavItem; ativo: boolean; recolhida: boolean }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      data-nav
      // `aria-current` é o que o leitor de tela anuncia; a cor sozinha não
      // informa qual item está aberto.
      aria-current={ativo ? 'page' : undefined}
      title={recolhida ? `${item.label} — ${item.hint}` : item.hint}
      className={cn(
        'relative flex items-center gap-2 rounded px-2 py-[5px] text-[13px] transition-all duration-150',
        'focus-visible:shadow-glow focus-visible:outline-none',
        recolhida && 'justify-center',
        ativo
          ? 'bg-white/[0.12] font-semibold text-white'
          : 'text-sidebar-foreground/65 hover:bg-white/[0.07] hover:text-sidebar-foreground',
      )}
    >
      {ativo && !recolhida ? (
        <span
          aria-hidden
          className="absolute inset-y-1.5 left-0 w-[3px] rounded-r-full"
          style={{ background: 'linear-gradient(to bottom, #C8D93A, #2BBFCF)' }}
        />
      ) : null}
      <Icon className={cn('h-4 w-4 flex-none', ativo ? 'text-primary' : '')} aria-hidden />
      {recolhida ? <span className="sr-only">{item.label}</span> : <span>{item.label}</span>}
    </Link>
  );
}

// ── cabeçalho ──────────────────────────────────────────────────────────────

function Header({ onAbrirGaveta }: { onAbrirGaveta: () => void }) {
  return (
    <header className="sticky top-0 z-30 flex h-11 flex-none items-center gap-2 border-b border-border bg-background/95 px-3 backdrop-blur-sm sm:px-4">
      <button
        type="button"
        onClick={onAbrirGaveta}
        aria-label="Abrir navegação"
        className="grid h-7 w-7 flex-none place-items-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:shadow-glow focus-visible:outline-none md:hidden"
      >
        <MenuIcon className="h-4 w-4" aria-hidden />
      </button>

      <GlobalSearch />

      <div className="ml-auto flex flex-none items-center gap-0.5">
        <NotificationBell />
        <ThemeMenu />
        <UserMenu />
      </div>
    </header>
  );
}

/**
 * Busca global.
 *
 * Hoje só encaminha o termo para a lista de chamados — o `q` da API casa
 * identificador exato (`I2603-0001`) e assunto por trecho. Quando houver
 * busca federada (conhecimento, itens de configuração, pessoas), o destino
 * muda aqui e o campo continua no mesmo lugar.
 */
function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [termo, setTermo] = useState('');

  const focar = useCallback(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    const aoTeclar = (event: globalThis.KeyboardEvent) => {
      const alvo = event.target;
      // Digitar "/" dentro de um campo é digitar uma barra, não abrir a busca.
      const editando =
        alvo instanceof HTMLElement &&
        (alvo.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(alvo.tagName));

      const atalhoK = event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey);
      if (atalhoK || (event.key === '/' && !editando && !event.metaKey && !event.ctrlKey)) {
        event.preventDefault();
        focar();
      }
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [focar]);

  function enviar(event: FormEvent) {
    event.preventDefault();
    const q = termo.trim();
    if (!q) return;
    void navigate({ to: '/chamados', search: { q } });
  }

  return (
    <form role="search" onSubmit={enviar} className="min-w-0 flex-1 sm:max-w-md">
      <label htmlFor="busca-global" className="sr-only">
        Buscar chamados por número ou assunto
      </label>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          ref={inputRef}
          id="busca-global"
          type="search"
          value={termo}
          onChange={(event) => setTermo(event.target.value)}
          placeholder="Buscar chamados…"
          className={cn(
            'h-7 w-full rounded border border-input/80 bg-muted/50 pl-7 pr-10 text-xs',
            'transition-colors placeholder:text-muted-foreground',
            'hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:shadow-glow focus-visible:outline-none',
          )}
        />
        <kbd
          aria-hidden
          className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:block"
        >
          /
        </kbd>
      </div>
      {/* Botão de envio invisível: o Enter num formulário de campo único já
          submete, mas leitor de tela e teclado virtual esperam encontrar o
          controle explícito. */}
      <button type="submit" className="sr-only">
        Buscar
      </button>
    </form>
  );
}

// ── notificações ──────────────────────────────────────────────────────────────

const CHAVES_NOTIF = { lista: ['notifications'] as const };

function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);
  const refEl = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: CHAVES_NOTIF.lista,
    queryFn: listarNotificacoes,
    refetchInterval: 30_000,
  });

  const lerTodas = useMutation({
    mutationFn: marcarTodasLidas,
    onSuccess: () => void qc.invalidateQueries({ queryKey: CHAVES_NOTIF.lista }),
  });

  const lerUma = useMutation({
    mutationFn: (id: string) => marcarLida(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: CHAVES_NOTIF.lista }),
  });

  // Fecha ao clicar fora
  useEffect(() => {
    if (!aberto) return;
    function aoClicarFora(e: MouseEvent) {
      if (refEl.current && !refEl.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [aberto]);

  const unread = data?.unreadCount ?? 0;
  const items = data?.items ?? [];

  function aoClicarItem(n: UserNotification) {
    if (!n.readAt) lerUma.mutate(n.id);
    if (n.linkPath) {
      setAberto(false);
      void navigate({ to: n.linkPath });
    }
  }

  function fmtTempo(iso: string) {
    const diff = Date.now() - new Date(iso).getTime();
    const min = Math.floor(diff / 60_000);
    if (min < 1) return 'agora';
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
  }

  return (
    <div ref={refEl} className="relative">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-label={`Notificações${unread > 0 ? ` — ${unread} não lidas` : ''}`}
        className="relative grid h-7 w-7 place-items-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:shadow-glow focus-visible:outline-none"
      >
        <Bell className="h-4 w-4" aria-hidden />
        {unread > 0 && (
          <span
            aria-hidden
            className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {aberto && (
        <div className="absolute right-0 top-9 z-50 w-80 overflow-hidden rounded-xl border border-border bg-background shadow-elevation">
          {/* cabeçalho do painel */}
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-xs font-semibold">Notificações</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => lerTodas.mutate()}
                disabled={lerTodas.isPending}
                className="text-[10px] text-primary hover:underline disabled:opacity-50"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          {/* lista */}
          <ul className="max-h-80 divide-y divide-border overflow-y-auto">
            {items.length === 0 ? (
              <li className="px-3 py-6 text-center text-xs text-muted-foreground">
                Nenhuma notificação
              </li>
            ) : (
              items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => aoClicarItem(n)}
                    className={cn(
                      'w-full px-3 py-2.5 text-left transition-colors hover:bg-accent',
                      !n.readAt && 'bg-primary/5',
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className={cn('text-xs', !n.readAt ? 'font-semibold' : 'font-medium')}>
                          {n.title}
                        </p>
                        <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{n.body}</p>
                      </div>
                      <div className="flex flex-none flex-col items-end gap-1">
                        <span className="text-[10px] text-muted-foreground">{fmtTempo(n.createdAt)}</span>
                        {!n.readAt && (
                          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-primary" />
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

const ICONE_DE_TEMA = { light: Sun, dark: Moon, system: Monitor } as const;

function ThemeMenu() {
  const mode = useThemeStore((estado) => estado.mode);
  const resolved = useThemeStore((estado) => estado.resolved);
  const setMode = useThemeStore((estado) => estado.setMode);
  const Icone = resolved === 'dark' ? Moon : Sun;

  return (
    <Menu
      label={`Tema: ${ROTULO_DE_TEMA[mode]}`}
      align="end"
      className="min-w-44"
      triggerClassName="grid h-7 w-7 place-items-center text-muted-foreground hover:bg-accent hover:text-foreground"
      trigger={<Icone className="h-4 w-4" aria-hidden />}
    >
      {(fechar) => (
        <>
          <MenuLabel>Tema</MenuLabel>
          {(['light', 'dark', 'system'] as const).map((opcao) => {
            const IconeOpcao = ICONE_DE_TEMA[opcao];
            return (
              <MenuItem
                key={opcao}
                icon={<IconeOpcao className="h-4 w-4" aria-hidden />}
                selected={mode === opcao}
                onClick={() => {
                  setMode(opcao);
                  fechar();
                }}
              >
                {ROTULO_DE_TEMA[opcao]}
              </MenuItem>
            );
          })}
        </>
      )}
    </Menu>
  );
}

function UserMenu() {
  const user = useAuthStore((estado) => estado.user);
  const logout = useAuthStore((estado) => estado.logout);
  const nome = user?.name ?? 'Sessão';

  return (
    <Menu
      label={`Conta de ${nome}`}
      align="end"
      triggerClassName="h-7 gap-1.5 rounded px-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
      trigger={
        <>
          <Avatar nome={nome} />
          <span className="hidden max-w-32 truncate text-sm font-medium text-foreground sm:block">
            {nome}
          </span>
          <ChevronDown className="h-4 w-4 flex-none" aria-hidden />
        </>
      }
    >
      {(fechar) => (
        <>
          <div className="px-2.5 py-2">
            <p className="truncate text-sm font-medium">{nome}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email ?? '—'}</p>
          </div>
          <MenuSeparator />
          <MenuItem
            icon={<UserRound className="h-4 w-4" aria-hidden />}
            disabled
            title="Ainda não construído"
          >
            Meu perfil (em breve)
          </MenuItem>
          <MenuSeparator />
          <MenuItem
            icon={<LogOut className="h-4 w-4" aria-hidden />}
            tone="danger"
            onClick={() => {
              // Sem devolver o foco ao gatilho: ele desaparece junto com a
              // casca, e o foco iria para o `<body>` de qualquer jeito.
              fechar(false);
              void logout();
            }}
          >
            Sair
          </MenuItem>
        </>
      )}
    </Menu>
  );
}

function Avatar({ nome }: { nome: string }) {
  const iniciais = nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <span
      aria-hidden
      className="grid h-6 w-6 flex-none place-items-center rounded-full gradient-primary text-[10px] font-bold text-white"
    >
      {iniciais || '?'}
    </span>
  );
}

// ── barra de status ────────────────────────────────────────────────────────

function StatusBar() {
  return (
    <div
      aria-hidden
      className="flex h-6 flex-none items-center border-t border-border/50 bg-muted/20 px-3 gap-3"
    >
      <span className="text-[10px] text-muted-foreground/40 select-none">Central Aptum</span>
      <span className="text-[10px] text-muted-foreground/25 select-none">·</span>
      <span className="text-[10px] text-muted-foreground/40 select-none">Service Desk · ITIL 4</span>
    </div>
  );
}

// ── carregamento inicial ───────────────────────────────────────────────────

/**
 * Esqueleto da casca, para o intervalo entre abrir a página e saber se há
 * sessão. Desenha lateral e cabeçalho no lugar definitivo, então quando o
 * conteúdo real entra nada se desloca.
 */
export function AppShellSkeleton() {
  return (
    <div className="flex h-full bg-background" role="status" aria-busy aria-live="polite">
      <span className="sr-only">Carregando a Central</span>
      <div className="hidden w-60 flex-none flex-col gap-2 border-r border-white/[0.07] bg-sidebar p-3 md:flex">
        <Skeleton className="mb-3 h-8 w-32" />
        {Array.from({ length: 6 }, (_, indice) => (
          <Skeleton key={indice} className="h-9 w-full" />
        ))}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-14 flex-none items-center gap-3 border-b border-border px-4">
          <Skeleton className="h-9 w-full max-w-md" />
          <Skeleton className="ml-auto h-9 w-9 rounded-md" />
          <Skeleton className="h-9 w-28 rounded-md" />
        </div>
        <div className="w-full space-y-4 px-4 py-4 sm:px-5 lg:px-6">
          <Skeleton className="h-8 w-56" />
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }, (_, indice) => (
              <Skeleton key={indice} className="h-28 w-full rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
